import './TranslationComponentConfig';
import { useState, useRef, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ContainerButtonStyled,
  ListItemButtonStyled,
  ListStyled,
  ListSubheaderStyled,
  MenuPopoverStyled,
  WorldIconContainerStyled,
} from './styles/TranslationComponentStyles';
import { useStudentAdvicedIdCache } from '../../../cache/advisor/studentIdReactiveVar';
import { kanbanJobTitleId, kanbanProficiencyLevel, kanbanSkillType, userMajors } from '../../../cache/cache';
import { renderNotification } from '../../../cache/learner/homeReactiveVars';
import { invalidMajor } from '../../../cache/learner/invalidMajorReactiveVar';
import { kanbanDataVar } from '../../../cache/learner/kanbanReactiveVar';
import { INVALID_MAJOR_ID } from '../../../constants/invalidMajorId';
import { FeatureFlagsContext } from '../../../contexts/FeatureFlagsContext';
import useKanbanDataHook from '../../../hooks/kanbanDataHook/useKanbanDataHook';
import useNotificationsHook from '../../../hooks/notificationsHook/useNotificationsHook';
import userAuth from '../../../hooks/userAuth';
import useStudentRefetch from '../../../hooks/useStudentRefetch';
import { useMutationChangePreferredLanguage } from '../../../operations/mutations/loginUser/changePreferredLanguage';
import { useLazyGetStudentMajors } from '../../../operations/query/students/useGetStudentMajors';
import { useLazyGetStudentNotification } from '../../../operations/query/students/useGetStudentNotification';
import { setCookie, getCookie } from '../../../utils/cookiesUtil';
import { tokenDecode } from '../../../utils/jwtUtils';
import { ROLE_ENUM } from '../../../views/Auth/Login/interfaces/IGoogleUser';
import AtomProgress from '../../atoms/AtomProgress';
import AtomTypography from '../../atoms/AtomTypography/AtomTypography';
import CheckIcon from '../../icons/svg/CheckIcon';
import ChevronDownIcon from '../../icons/svg/ChevronDownIcon';
import ChevronUpIcon from '../../icons/svg/ChevronUpIcon';
import EarthIcon from '../../icons/svg/EarthIcon';
import { styleConfigs } from '../../resources/styleConfigs';

export interface IHandleListItems {
  id: number;
  language: string;
  languageId: string;
}

const TranslationComponent: React.FC = () => {
  const { t } = useTranslation('TranslationComponent');
  const { features } = useContext(FeatureFlagsContext);
  const { auth, setToken } = userAuth();
  const isLearner = auth?.userRole === ROLE_ENUM.STUDENT;
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [isLoadingPreferredLanguage, setIsLoadingPreferredLanguage] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<string>(t('CURRENT_LANGUAGE'));
  const [selectedLanguageId, setSelectedLanguageId] = useState<string>(
    getCookie('preferredLanguage') ?? auth?.preferredLanguage ?? features?.qsemLangDefaultLanguage?.language ?? 'en',
  );
  const [activeBackground, setActiveBackground] = useState<number | null>(null);
  const [changePreferedLanguageHandler] = useMutationChangePreferredLanguage();
  const languageList = t('POPOVER_CONTENT', { returnObjects: true });
  const { operations: notificationOps } = useNotificationsHook();
  const { operations } = useKanbanDataHook(kanbanDataVar, kanbanSkillType, kanbanJobTitleId, kanbanProficiencyLevel);
  const [getData, { data: majorResponse }] = useLazyGetStudentMajors(true);
  const { studentId: advisedStudentId } = useStudentAdvicedIdCache();
  const currentStudentId = advisedStudentId ?? auth?.student?.id ?? '';
  const { refetchStudentData, refetchPathways } = useStudentRefetch();
  const { i18n } = useTranslation();

  const [getDataNotifications, { data: dataNotifications }] = useLazyGetStudentNotification(false);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleLanguageChangeMutation = async (preferredLanguage: string): Promise<void> => {
    setIsLoadingPreferredLanguage(true);

    if (!auth) {
      const preferredLanguageFromCookie = getCookie('preferredLanguage') ?? features.qsemLangDefaultLanguage?.language;

      i18n.changeLanguage(preferredLanguageFromCookie);
      setSelectedLanguageId(preferredLanguageFromCookie ?? '');
      setIsLoadingPreferredLanguage(false);

      return;
    }

    try {
      const { data } = await changePreferedLanguageHandler({
        variables: {
          preferredLanguage,
        },
      });

      const token = data?.changePreferredLanguage?.jwtToken ?? '';
      const tokenDecoded = tokenDecode(token);

      setToken(tokenDecoded, tokenDecoded.expiresIn, token);
      i18n.changeLanguage(preferredLanguage);

      refetchStudentData?.();
      refetchPathways?.();

      if (currentStudentId) {
        getData({
          variables: {
            studentId: currentStudentId,
          },
        });

        operations.refetchKanbanData();
        getDataNotifications({
          variables: {
            studentId: currentStudentId,
          },
          fetchPolicy: 'network-only',
        });
      }
      setIsLoadingPreferredLanguage(false);
    } catch (error) {
      setIsLoadingPreferredLanguage(false);
      notificationOps.addNotification('danger', t('SOMETHING_WENT_WRONG'), t('ERROR'));
    }
  };

  const selectedLanguage = useMemo(() => {
    if (!Array.isArray(languageList)) {
      return t('CURRENT_LANGUAGE');
    }

    const languageToShow = (languageList as IHandleListItems[])?.find((item) => item.languageId === selectedLanguageId);

    return languageToShow?.language ?? t('CURRENT_LANGUAGE');
  }, [languageList, selectedLanguageId, t]);

  useEffect(() => {
    if (dataNotifications?.getStudentNotification) {
      renderNotification(dataNotifications?.getStudentNotification);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataNotifications]);

  useEffect(() => {
    if (selectedLanguageId !== auth?.preferredLanguage) {
      handleLanguageChangeMutation(selectedLanguageId);
    }

    const languageListNormalized = Array.isArray(languageList) ? languageList : [];

    const languageToShow = (languageListNormalized as IHandleListItems[])?.find(
      (item) => item.languageId === selectedLanguageId,
    );

    setSelectedItem(languageToShow?.language ?? t('CURRENT_LANGUAGE'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguageId]);

  const handleListItems = (listItems: IHandleListItems[]): JSX.Element[] =>
    Array.isArray(listItems)
      ? listItems.map((item) => (
          <ListItemButtonStyled
            data-testid={item.language}
            data-cy={`translation-lang-${item.languageId}`}
            key={item.id}
            role="option"
            aria-selected={selectedItem === item.language || activeBackground === item.id}
            onClick={() => {
              handleClose();
              setSelectedItem(item.language);
              setActiveBackground(item.id);
              setSelectedLanguageId(item.languageId);
              setCookie('preferredLanguage', item.languageId);
            }}
            activebackground={selectedItem === item.language || activeBackground === item.id ? 1 : 0}
            activeborderradius={item.id === 2 ? 1 : 0}
          >
            {item.language}
            {(selectedItem === item.language || activeBackground === item.id) && <CheckIcon />}
          </ListItemButtonStyled>
        ))
      : [];

  useEffect(() => {
    if (majorResponse?.getStudentMajors) {
      invalidMajor(
        majorResponse?.getStudentMajors[0]?.tblMajor?.id === INVALID_MAJOR_ID ||
          !majorResponse?.getStudentMajors[0]?.tblMajor,
      );
      const majors = majorResponse?.getStudentMajors.map((item) => ({
        name: item.tblMajor?.name ?? '',
      }));

      userMajors(majors);
    }
  }, [majorResponse]);

  return (
    <>
      <ContainerButtonStyled
        islearner={isLearner ? 1 : 0}
        data-testid="test_pop_button"
        data-cy="translation-pop-button"
        ref={anchorRef}
        onClick={handleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? 'language-selector-listbox' : undefined}
        aria-label={t('LANGUAGE_SELECTOR_BUTTON_ARIA_LABEL', { language: selectedLanguage })}
      >
        {isLoadingPreferredLanguage ? (
          <AtomProgress
            visible
            iconVariant="small"
            layout="inline-silent"
            background="transparent"
            data-testid="circular_loader"
          />
        ) : (
          <WorldIconContainerStyled>
            <EarthIcon data-testid="test_world" data-cy="translation-world-icon" aria-hidden="true" />
          </WorldIconContainerStyled>
        )}
        <AtomTypography
          component="span"
          fontWeight={700}
          fontSize={styleConfigs.fonts.fontSize14}
          sx={{
            paddingBlock: styleConfigs.marginPadding.custom11rem,
            paddingInline: `${styleConfigs.marginPadding.custom8px} ${styleConfigs.marginPadding.custom10px}`,
          }}
        >
          {selectedLanguage}
        </AtomTypography>
        {open ? (
          <ChevronUpIcon data-testid="chevron_up_icon" data-cy="translation-chevron-up" />
        ) : (
          <ChevronDownIcon data-testid="chevron_down_icon" data-cy="translation-chevron-down" />
        )}
      </ContainerButtonStyled>

      <MenuPopoverStyled
        anchorEl={anchorRef.current}
        open={open}
        onClose={handleClose}
        arrowPopover={false}
        direction="right"
      >
        <ListStyled
          id="language-selector-listbox"
          role="listbox"
          aria-label={t('TITLE_POPOVER')}
          subheader={<ListSubheaderStyled>{t('TITLE_POPOVER')}</ListSubheaderStyled>}
        >
          {handleListItems(t('POPOVER_CONTENT', { returnObjects: true }) as IHandleListItems[])}
        </ListStyled>
      </MenuPopoverStyled>
    </>
  );
};

export default TranslationComponent;
